import { useTranslation } from 'react-i18next';
import { Dialog } from '@noor/ui';
import type {
  ChangeRequest,
  ModerationEntityType,
  Translate,
} from '@/entities/moderation';
import { useModerationReview } from '../model/use-moderation-review';
import { SnapshotReviewModal } from './detail/snapshot-review-modal';
import { UpdateReviewModal } from './detail/update-review-modal';

interface ModerationDetailModalProps {
  entityType: ModerationEntityType;
  request: ChangeRequest | null;
  onClose: () => void;
}

/**
 * Review modal for one change request. It branches on exactly two axes,
 * both read from the request itself (never from the card, so the right
 * view renders at once and only its content waits on the card):
 *
 * | operation | pending                         | approved / rejected / closed     |
 * | --------- | ------------------------------- | -------------------------------- |
 * | create    | proposed entity + decision      | proposed entity + verdict        |
 * | update    | live vs 1–2 proposals + decision| requested changes + live, verdict|
 * | delete    | entity being removed + decision | removed entity + verdict         |
 */
export function ModerationDetailModal({
  entityType,
  request,
  onClose,
}: ModerationDetailModalProps) {
  if (request === null) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <ModerationDetailModalBody
        key={request.id}
        entityType={entityType}
        request={request}
        onClose={onClose}
      />
    </Dialog>
  );
}

function ModerationDetailModalBody({
  entityType,
  request,
  onClose,
}: {
  entityType: ModerationEntityType;
  request: ChangeRequest;
  onClose: () => void;
}) {
  const { t } = useTranslation('moderation');
  const translate: Translate = (key, fallback) => t(key, { defaultValue: fallback });
  const review = useModerationReview(entityType, request);

  if (review.operation === 'update' && review.decision === 'pending') {
    return (
      <UpdateReviewModal
        entityType={entityType}
        review={review}
        t={translate}
        onClose={onClose}
      />
    );
  }
  return (
    <SnapshotReviewModal
      entityType={entityType}
      review={review}
      t={translate}
      onClose={onClose}
    />
  );
}
